import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FleetMapView from "../dashboard/FleetMapView";
import {
  BarPanel,
  CUSTOM_DATE_OPTION,
  ChartDateSelect,
  getReportRows,
} from "../vehicle/DashboardGraph";
import { reportsApi, getOrganizationId } from "../manageData/reportsApi";
import {
  EMPTY_CHART_ROWS,
  useChartPeriod,
  useChartValue,
} from "../common/useChartPeriod";
import { buildRouteDashboardCharts } from "./routeDashboardUtils";

const loadRouteTripRows = (organizationId, period) =>
  reportsApi
    .getTripReport({
      orgId: organizationId || getOrganizationId(),
      category: "routes",
      selectedIds: [],
      selectAll: true,
      dateOption: period.dateOption,
      customStartDate:
        period.dateOption === CUSTOM_DATE_OPTION
          ? period.customStart
          : undefined,
      customEndDate:
        period.dateOption === CUSTOM_DATE_OPTION ? period.customEnd : undefined,
    })
    .then((report) => getReportRows(report));

const loadRouteDeviationRows = (organizationId, period) =>
  reportsApi
    .getRouteDeviationReport({
      orgId: organizationId || getOrganizationId(),
      category: "routes",
      selectedIds: [],
      selectAll: true,
      dateOption: period.dateOption,
      customStartDate:
        period.dateOption === CUSTOM_DATE_OPTION
          ? period.customStart
          : undefined,
      customEndDate:
        period.dateOption === CUSTOM_DATE_OPTION ? period.customEnd : undefined,
    })
    .then((report) => getReportRows(report));

function ChartDateControl({ period }) {
  return (
    <ChartDateSelect
      dateOption={period.dateOption}
      onDateOptionChange={period.setPeriod}
      customStartDate={period.customStart}
      customEndDate={period.customEnd}
      onCustomRangeChange={period.setCustomRange}
    />
  );
}

function AdherenceGaugePanel({
  charts,
  error = "",
  extra = null,
  loading = false,
}) {
  const { t } = useTranslation();
  const onTrack = charts.adherenceRows[0]?.value || 0;
  const offTrack = charts.adherenceRows[1]?.value || 0;
  const total = onTrack + offTrack;
  const percent =
    charts.adherencePercent ??
    (total ? Math.round((onTrack / total) * 100) : 0);
  const radius = 86;
  const circumference = Math.PI * radius;
  const onTrackLength = total ? (onTrack / total) * circumference : 0;
  const offTrackLength = Math.max(circumference - onTrackLength, 0);
  const performingWell = onTrack;
  const needAttention = offTrack;

  return (
    <section className="vehicle-dashboard-card route-adherence-card">
      <div className="vehicle-dashboard-panel-header">
        <h3>{t("Route Adherence")}</h3>
        {extra}
      </div>
      {loading ? (
        <span className="vehicle-dashboard-empty">
          {t("Loading route adherence…")}
        </span>
      ) : total ? (
        <>
          <div className="route-adherence-gauge">
            <svg
              viewBox="0 0 220 130"
              className="route-adherence-gauge-svg"
              aria-hidden="true"
            >
              <path
                d="M 24 118 A 86 86 0 0 1 196 118"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="22"
                pathLength={circumference}
              />
              <path
                d="M 24 118 A 86 86 0 0 1 196 118"
                fill="none"
                stroke="#22c55e"
                strokeWidth="22"
                pathLength={circumference}
                strokeDasharray={`${onTrackLength} ${circumference}`}
              />
              <path
                d="M 24 118 A 86 86 0 0 1 196 118"
                fill="none"
                stroke="#ef4444"
                strokeWidth="22"
                pathLength={circumference}
                strokeDasharray={`${offTrackLength} ${circumference}`}
                strokeDashoffset={-onTrackLength}
              />
            </svg>
            <div className="route-adherence-gauge-center">
              <strong>{percent}%</strong>
              <span>{t("ROUTE ADHERENCE")}</span>
            </div>
          </div>
          <div className="route-adherence-legend">
            <div>
              <i style={{ backgroundColor: "#22c55e" }} />
              <span>{t("On Track")}</span>
              <strong>
                {total ? Math.round((onTrack / total) * 100) : 0}% ({onTrack})
              </strong>
            </div>
            <div>
              <i style={{ backgroundColor: "#ef4444" }} />
              <span>{t("Off Track")}</span>
              <strong>
                {total ? Math.round((offTrack / total) * 100) : 0}% ({offTrack})
              </strong>
            </div>
          </div>
          <div className="route-adherence-footnote">
            {`${performingWell} of ${total} routes are performing well.`}
            <br />
            {`${needAttention} ${needAttention === 1 ? "route needs" : "routes need"} attention.`}
          </div>
        </>
      ) : (
        <span className="vehicle-dashboard-empty">
          {error ? t(error) : t("No trip adherence data yet")}
        </span>
      )}
    </section>
  );
}

function DelayedRoutesPanel({
  rows,
  error = "",
  onViewAll,
  extra = null,
  loading = false,
}) {
  const { t } = useTranslation();

  return (
    <section className="vehicle-dashboard-card">
      <div className="vehicle-dashboard-panel-header">
        <h3>{t("Top Delayed Routes")}</h3>
        <div className="vehicle-dashboard-header-extras">
          {extra}
          {onViewAll ? (
            <button type="button" onClick={onViewAll}>
              {t("VIEW ALL")}
            </button>
          ) : null}
        </div>
      </div>
      <div className="route-delayed-list">
        {loading ? (
          <span className="vehicle-dashboard-empty">
            {t("Loading delayed routes…")}
          </span>
        ) : rows.length ? (
          rows.map((row, index) => (
            <div className="route-delayed-row" key={`${row.label}-${index}`}>
              <div>
                <strong>{row.label}</strong>
                <span>{row.subtitle}</span>
              </div>
              <em>
                {row.minutes || 0} {t("min")}
                <small>{t("Delay")}</small>
              </em>
            </div>
          ))
        ) : (
          <span className="vehicle-dashboard-empty">
            {error ? t(error) : t("No delayed routes for this period")}
          </span>
        )}
      </div>
    </section>
  );
}

function RouteDashboardGraph({
  routes = [],
  tripRows = [],
  deviationRows = [],
  error = "",
  onViewDelayedRoutes,
  dateOption = "today",
  customStartDate = "",
  customEndDate = "",
  organizationId,
}) {
  const { t } = useTranslation();
  const adherencePeriod = useChartPeriod(
    dateOption,
    customStartDate,
    customEndDate,
  );
  const delayedPeriod = useChartPeriod(
    dateOption,
    customStartDate,
    customEndDate,
  );
  const deviationPeriod = useChartPeriod(
    dateOption,
    customStartDate,
    customEndDate,
  );
  const delayPeriod = useChartPeriod(
    dateOption,
    customStartDate,
    customEndDate,
  );
  const loadAdherenceTrips = useCallback(
    () => loadRouteTripRows(organizationId, adherencePeriod),
    [
      organizationId,
      adherencePeriod.dateOption,
      adherencePeriod.customStart,
      adherencePeriod.customEnd,
    ],
  );
  const loadAdherenceDeviations = useCallback(
    () => loadRouteDeviationRows(organizationId, adherencePeriod),
    [
      organizationId,
      adherencePeriod.dateOption,
      adherencePeriod.customStart,
      adherencePeriod.customEnd,
    ],
  );
  const loadDelayedTrips = useCallback(
    () => loadRouteTripRows(organizationId, delayedPeriod),
    [
      organizationId,
      delayedPeriod.dateOption,
      delayedPeriod.customStart,
      delayedPeriod.customEnd,
    ],
  );
  const loadDeviations = useCallback(
    () => loadRouteDeviationRows(organizationId, deviationPeriod),
    [
      organizationId,
      deviationPeriod.dateOption,
      deviationPeriod.customStart,
      deviationPeriod.customEnd,
    ],
  );
  const loadDelayTrips = useCallback(
    () => loadRouteTripRows(organizationId, delayPeriod),
    [
      organizationId,
      delayPeriod.dateOption,
      delayPeriod.customStart,
      delayPeriod.customEnd,
    ],
  );
  const adherenceTrips = useChartValue(
    adherencePeriod,
    tripRows,
    loadAdherenceTrips,
    EMPTY_CHART_ROWS,
  );
  const adherenceDeviations = useChartValue(
    adherencePeriod,
    deviationRows,
    loadAdherenceDeviations,
    EMPTY_CHART_ROWS,
  );
  const delayedTrips = useChartValue(
    delayedPeriod,
    tripRows,
    loadDelayedTrips,
    EMPTY_CHART_ROWS,
  );
  const chartDeviations = useChartValue(
    deviationPeriod,
    deviationRows,
    loadDeviations,
    EMPTY_CHART_ROWS,
  );
  const delayTrips = useChartValue(
    delayPeriod,
    tripRows,
    loadDelayTrips,
    EMPTY_CHART_ROWS,
  );
  const charts = useMemo(
    () => buildRouteDashboardCharts({ routes, tripRows, deviationRows }),
    [routes, tripRows, deviationRows],
  );
  const adherenceCharts = useMemo(
    () =>
      buildRouteDashboardCharts({
        routes,
        tripRows: adherenceTrips.value,
        deviationRows: adherenceDeviations.value,
      }),
    [adherenceDeviations.value, adherenceTrips.value, routes],
  );
  const delayedCharts = useMemo(
    () =>
      buildRouteDashboardCharts({
        routes,
        tripRows: delayedTrips.value,
        deviationRows: [],
      }),
    [delayedTrips.value, routes],
  );
  const deviationCharts = useMemo(
    () =>
      buildRouteDashboardCharts({
        routes,
        tripRows: [],
        deviationRows: chartDeviations.value,
      }),
    [chartDeviations.value, routes],
  );
  const delayCharts = useMemo(
    () =>
      buildRouteDashboardCharts({
        routes,
        tripRows: delayTrips.value,
        deviationRows: [],
      }),
    [delayTrips.value, routes],
  );
  const needsRange = (period) =>
    period.dateOption === CUSTOM_DATE_OPTION &&
    !(period.customStart && period.customEnd);
  const chartError = (period, queryError) => {
    if (needsRange(period)) return "";
    return period.matchesPage ? error : queryError;
  };

  return (
    <div className="route-dashboard">
      <div className="route-dashboard-top">
        <section className="vehicle-dashboard-card vehicle-dashboard-live-card vehicle-dashboard-live-card--map">
          <div className="vehicle-dashboard-map vehicle-dashboard-map--reusable">
            <FleetMapView filterBy="routes" />
          </div>
        </section>

        <AdherenceGaugePanel
          charts={adherenceCharts}
          error={
            needsRange(adherencePeriod)
              ? "Pick a start and end date"
              : chartError(
                  adherencePeriod,
                  adherenceTrips.error || adherenceDeviations.error,
                )
          }
          loading={
            !needsRange(adherencePeriod) &&
            (adherenceTrips.loading || adherenceDeviations.loading)
          }
          extra={<ChartDateControl period={adherencePeriod} />}
        />
        <DelayedRoutesPanel
          rows={delayedCharts.topDelayedRoutes}
          error={
            needsRange(delayedPeriod)
              ? "Pick a start and end date"
              : chartError(delayedPeriod, delayedTrips.error)
          }
          loading={!needsRange(delayedPeriod) && delayedTrips.loading}
          onViewAll={onViewDelayedRoutes}
          extra={<ChartDateControl period={delayedPeriod} />}
        />
      </div>

      <div className="route-dashboard-bottom">
        <BarPanel
          title="Vehicles by Routes"
          rows={charts.vehiclesByRoute}
          valueLabel="vehicles"
          emptyMessage={error ? t(error) : "No vehicles assigned to routes yet"}
        />
        <BarPanel
          title="Route Deviations"
          rows={deviationCharts.routeDeviations}
          valueLabel=""
          extra={<ChartDateControl period={deviationPeriod} />}
          emptyMessage={
            chartDeviations.loading
              ? "Loading route deviations…"
              : needsRange(deviationPeriod)
                ? "Pick a start and end date"
                : chartError(deviationPeriod, chartDeviations.error) ||
                  "No route deviations recorded yet"
          }
        />
        <BarPanel
          title="Avg. Delay by Route (mins)"
          rows={delayCharts.delayByRoute}
          valueLabel=""
          extra={<ChartDateControl period={delayPeriod} />}
          emptyMessage={
            delayTrips.loading
              ? "Loading route delay…"
              : needsRange(delayPeriod)
                ? "Pick a start and end date"
                : chartError(delayPeriod, delayTrips.error) ||
                  "No delayed trips for this period"
          }
        />
      </div>
    </div>
  );
}

export default memo(RouteDashboardGraph);
